import { patchConversation, talkToCoach } from 'api/v2/AI';
import classNames from 'classnames';
import { Button } from 'components/atoms/Button';
import AIButtonConsentCheck from 'components/molecules/AIButtonConsentCheck';
import AIButtonConsentUpdate from 'components/molecules/AIButtonConsentUpdate';
import useGetCurrentCustomerData from 'hooks/Authentication/useGetCurrentCustomerData';
import getIntercom from 'hooks/intercom.hook';
import useConfirm from 'hooks/useConfirm';
import useResolveFileURL from 'hooks/useResolveFileURL';
import type { FC } from 'react';
import { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import { useMutation } from 'react-query';
import {
  ConversationMetadata,
  MessageTypeEnum,
  type Message,
} from 'types/ai.types';
import { UserAiUsageConsentEnum } from 'types/users.types';
import { openInNewTab } from 'utils/helpers';
import { useAIChatContext } from './AIChat.context';
import AIChatMessageAvatar from './AIChatMessageAvatar';
import { sharedMarkdownComponents } from './utils';

type AIChatMessageItemProps = {
  message: Message;
  isLastAssistantMessage?: boolean;
  isSmall?: boolean;
  isFullWidth?: boolean;
  renderMessage?: (message: Message) => JSX.Element;
};

const AIChatMessageContent: FC<AIChatMessageItemProps> = ({
  message,
  isSmall,
  renderMessage,
}) => (
  <div className={classNames({ 'text-sm': isSmall })}>
    {renderMessage
      ? renderMessage(message)
      : message?.content?.split('\n').map((paragraph, i) => (
          <ReactMarkdown
            key={message._id + (paragraph || i)}
            components={sharedMarkdownComponents}
          >
            {paragraph}
          </ReactMarkdown>
        ))}
  </div>
);

const AIChatMessageConsentRequired: FC<AIChatMessageItemProps> = (props) => {
  const { t } = useTranslation();
  const { handleRegenerateMessage, isLoading, streamedData } =
    useAIChatContext();

  const messageId = props.message._id;

  return (
    <div className="flex flex-col">
      <AIChatMessageContent {...props} />
      {!isLoading && !streamedData && (
        <div className="flex flex-row space-x-2 justify-end">
          <AIButtonConsentCheck
            structure="text"
            className="opacity-75"
            onClick={() => handleRegenerateMessage(messageId, 'allow_access')}
            color="primary"
          >
            {t('transactions.more_options')}
          </AIButtonConsentCheck>

          <AIButtonConsentUpdate
            onUpdate={() => handleRegenerateMessage(messageId, 'allow_access')}
            requiredConsent={UserAiUsageConsentEnum.allow}
            disabled={isLoading || !!streamedData}
            className="self-end mt-1"
            structure="primary"
            color="taxes"
            size="xs"
          >
            {t('settings.ai_usage_consent.options.allow.label')}
          </AIButtonConsentUpdate>
        </div>
      )}
    </div>
  );
};

const AIChatMessageTalkToCoach: FC<AIChatMessageItemProps> = (props) => {
  const { conversationId, conversation } = useAIChatContext();
  const { t } = useTranslation();
  const { showNewMessage } = getIntercom();

  const { mutateAsync: talkToTaxCoach } = useMutation(() =>
    talkToCoach(conversationId, props.message?._id),
  );

  const { mutateAsync: patchConversationIntercomShare } = useMutation(() =>
    patchConversation(conversationId, { intercomShareConversation: true }),
  );

  const [confirmContent, confirmAPI] = useConfirm({
    className: 'text-center',
    buttonsWrapperClassName: 'justify-center',
    okText: t('yes'),
    cancelText: t('no'),
  });

  const handleTalkToCoachConfirm = (forwardChat: boolean) => {
    const content = forwardChat
      ? conversation?.messages.reduce((acc, { role, content }) => {
          const user = role === 'user' ? 'User' : 'AI Assistant';
          acc += `${user}: ${content}\n\n---\n\n`;
          return acc;
        }, '')
      : undefined;

    showNewMessage(t('ai_bot.talk_to_coach.message_history', { content }));
    talkToTaxCoach();
    confirmAPI.close();
  };

  const handleTalkToCoach = () =>
    confirmAPI.open({
      message: t('ai_bot.talk_to_tax_coach.share_conversation'),
      onConfirm: async () => {
        handleTalkToCoachConfirm(true);
        await patchConversationIntercomShare();
      },
      onDecline: () => handleTalkToCoachConfirm(false),
    });

  return (
    <>
      {confirmContent}
      <div className="flex flex-col">
        <AIChatMessageContent {...props} />
        <Button
          className="self-end mt-2"
          onClick={handleTalkToCoach}
          structure="text"
        >
          {t('talk_to_an_expert')}
        </Button>
      </div>
    </>
  );
};

type AIChatMetadatFileProps = {
  metadata: ConversationMetadata;
};

const AIChatMetadatFile: FC<AIChatMetadatFileProps> = ({ metadata }) => {
  const { data: fileUrl } = useResolveFileURL(
    metadata?.data.filePath as string,
  );

  const onClick = useCallback(() => {
    if (!fileUrl?.url) return;
    openInNewTab(fileUrl.url);
  }, [fileUrl?.url]);

  return (
    <Button
      className="flex flex-row items-center gap-2 rounded-md bg-white p-2.5 mb-3 font-bold cursor-pointer"
      onClick={onClick}
      asDefault
    >
      <p className="text-[#EF4444] bg-[#F6F6F6] h-9 w-9 flex items-center justify-center rounded-md border-b border-[#EBEBEB]">
        {metadata?.documentType === 'pdf' ? 'PDF' : 'IMG'}
      </p>
      <p>{metadata.data.originalFilename}</p>
    </Button>
  );
};

const AIChatMessageItem: FC<AIChatMessageItemProps & { className?: string }> =
  memo(function Message({
    isLastAssistantMessage,
    isFullWidth = true,
    className,
    ...props
  }) {
    const { data: user } = useGetCurrentCustomerData();
    const { data: picture, isLoading } = useResolveFileURL(
      user?.photoPath as string,
    );

    const { metadataItemsMap, streamedData } = useAIChatContext();
    const messageMetadata = props.message.metadataId
      ? metadataItemsMap[props.message.metadataId]
      : undefined;

    const { role, type: messageType, llm_unknown_response } = props.message;

    const dispatchContent = () => {
      if (llm_unknown_response && isLastAssistantMessage) {
        return messageType === MessageTypeEnum.dataAccessForm ? (
          <AIChatMessageConsentRequired {...props} />
        ) : (
          <AIChatMessageTalkToCoach {...props} />
        );
      }

      return <AIChatMessageContent {...props} />;
    };

    return (
      <div
        className={classNames(
          'text-left flex flex-col mt-3  py-4 rounded-10 text-primary items-start',
          role === 'user' ? 'bg-primary-50 !w-3/4' : 'bg-transparent',
          isFullWidth ? 'w-full' : 'w-[90%]',
          role === 'user' ? 'self-end' : 'self-start',
          !props.isSmall || role === 'user' ? 'px-4' : '',
          className,
        )}
      >
        {(messageMetadata?.documentType === 'pdf' ||
          messageMetadata?.documentType === 'image') && (
          <AIChatMetadatFile metadata={messageMetadata} />
        )}

        <div className="flex flex-row items-start gap-2">
          <AIChatMessageAvatar
            role={role}
            isSmall={!!props.isSmall}
            isLoading={isLoading}
            isStreaming={!!streamedData}
          />
          <div className="flex-1">{dispatchContent()}</div>
        </div>
      </div>
    );
  });

export default AIChatMessageItem;
